/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { RefObject } from 'preact';

/**
 * Return the dataset info of target element.
 * @param target
 * @param rootRef
 * @returns
 */
export function getElementData(rootRef: RefObject<Element>, target: HTMLElement) {
  let currentTarget = target;

  while (currentTarget != rootRef.current) {
    const objectId = currentTarget.dataset['ojPrivateObject'];
    if (objectId) {
      return currentTarget.dataset;
    }
    currentTarget = currentTarget.parentElement!;
  }
  return null;
}

/**
 * Parses the groupIndex, seriesIndex and ItemIndex from the fiven DOMStringMap.
 * @param infoMap
 */
function parseInfo(infoMap: DOMStringMap) {
  return {
    groupIndex: infoMap['ojPrivateGroupIndex']
      ? parseInt(infoMap['ojPrivateGroupIndex'], 10)
      : undefined,
    seriesIndex: infoMap['ojPrivateSeriesIndex']
      ? parseInt(infoMap['ojPrivateSeriesIndex'], 10)
      : undefined,
    itemIndex: infoMap['ojPrivateItemIndex']
      ? parseInt(infoMap['ojPrivateItemIndex'], 10)
      : undefined,
    levelIndex: infoMap['ojPrivateLevelIndex']
      ? parseInt(infoMap['ojPrivateLevelIndex'], 10)
      : undefined,
    refObjId: infoMap['ojPrivateRefObjId']
  };
}

/**
 * Returns the group or item info.
 * @param rootRef
 * @param event
 * @returns
 */
export function getInfo(rootRef: RefObject<HTMLElement>, target: HTMLElement) {
  const elDomMap = getElementData(rootRef, target);
  if (!elDomMap) {
    return;
  }
  return parseInfo(elDomMap);
}

/**
 * Prevent defaults and stops propagation of event.
 * @param event
 */
export function cancelEvent(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Compares if two sets are equal.
 */
export const isEqual = <T>(setA: Set<T>, setB: Set<T>) => {
  return setA.size === setB.size && [...setA].every((value) => setB.has(value));
};
